import {
  HomeEntityType,
  IssueType,
  useCurrentWorkspace,
  useCustomQuery,
} from '@gripp/shared-logic';
import { rangeToDates } from './useHomeActivityMap';
import { TimeRange } from '../types';

/**
 * Filter-panel facets forwarded into HomeActivityFilter. The resolver honours
 * `groupIds` on the asset layer (ASSET/ROUTINE/PRODUCT modes) and `labelIds` /
 * `issueType` on the activity layer (ACTIVITY mode); the caller passes only the
 * facets relevant to the active mode so the cache key stays stable across modes.
 */
export interface HomeEntityPointsFacets {
  personAccountId?: string | null;
  groupIds?: string[] | null;
  labelIds?: string[] | null;
  severityBuckets?: string[] | null;
  issueTypes?: IssueType[] | null;
}

/**
 * One map point for the plotting-mode toggle. Mirrors the generated
 * `HomeMapPoint`. `status` is a display label (asset status, or the
 * severity-derived activity bucket 'urgent'/'pending'/'solved'); the FE assigns
 * the marker colour from it.
 */
export interface HomeMapPoint {
  id: string;
  name: string;
  lat: number;
  lng: number;
  status: string | null;
}

const HOME_ENTITY_POINTS_QUERY = `
  query HomeEntityPoints($filter: HomeActivityFilter, $entityType: HomeEntityType!) {
    homeEntityPoints(filter: $filter, entityType: $entityType) {
      id
      name
      lat
      lng
      status
    }
  }
`;

/**
 * Fetches individual entity points for the map plotting-mode toggle, scoped to
 * the workspace + range. The resting map passes ASSET; selecting a card passes
 * that card's entity. Passing `null` disables the query (defensive — the map
 * always supplies a mode).
 *
 * Root query — reads `data.homeEntityPoints` directly. REAL and
 * workspace-scoped; ASSET/ACTIVITY can return a large bounded set, so the map
 * renders these clustered.
 */
export function useHomeEntityPoints(
  range: TimeRange,
  entityType: HomeEntityType | null,
  /**
   * Filter facets relevant to the active mode (see HomeEntityPointsFacets).
   * The People-card person filter, the group facet (asset layer), and the
   * label / issue-type facets (activity layer) all arrive here.
   */
  facets?: HomeEntityPointsFacets
): {
  data: HomeMapPoint[] | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { startDate, endDate } = rangeToDates(range);

  const personAccountId = facets?.personAccountId ?? null;
  // Empty arrays carry no filter intent; normalise to undefined so they neither
  // alter the cache key nor reach the resolver.
  const groupIds = facets?.groupIds?.length ? facets.groupIds : undefined;
  const labelIds = facets?.labelIds?.length ? facets.labelIds : undefined;
  const severityBuckets = facets?.severityBuckets?.length
    ? facets.severityBuckets
    : undefined;
  const issueTypes = facets?.issueTypes?.length ? facets.issueTypes : undefined;

  const { data, isLoading, error } = useCustomQuery({
    cacheKey: [
      'homeEntityPoints',
      workspaceId,
      range,
      entityType,
      personAccountId,
      groupIds,
      labelIds,
      severityBuckets,
      issueTypes,
    ],
    query: HOME_ENTITY_POINTS_QUERY,
    variables: {
      filter: {
        // Scope to the current (possibly impersonated) workspace; the query is
        // gated on workspaceId below so it is always present (GRIPP #2859).
        workspaceId,
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        ...(personAccountId ? { personAccountId } : {}),
        ...(groupIds ? { groupIds } : {}),
        ...(labelIds ? { labelIds } : {}),
        ...(severityBuckets ? { severityBuckets } : {}),
        ...(issueTypes ? { issueTypes } : {}),
      },
      entityType,
    },
    options: {
      // Only fetch when a plotting mode is active and the workspace is known.
      enabled: !!workspaceId && !!entityType,
      staleTime: 60_000,
    },
  });

  return { data: data?.homeEntityPoints, isLoading, error: error as Error | null };
}
